import headMaleFront from '../../../../assets/images/body/head-male-front.png';
import headFemaleFront from '../../../../assets/images/body/head-female-front.png';
import { BODY_SILHOUETTE_VIEWBOX, getBodySilhouetteFigure } from '../../../../components/bodySilhouette/bodySilhouetteFigures';

/**
 * Figuras do mapa de aplicação: rosto (de frente) e corpo (frente e costas).
 *
 * O rosto usa `head-*-front.png` (quadro 1024 x 1536, fundo transparente);
 * o corpo reaproveita a silhueta das medidas corporais (quadro 358 x 960).
 * As marcações são gravadas em frações do quadro de cada vista, então trocar
 * uma imagem exige recortá-la no mesmo quadro. Sem sexo no cadastro vale a
 * figura masculina.
 */

export const APPLICATION_VIEWS = Object.freeze({ FACE: 'face', FRONT: 'front', BACK: 'back' });

export const APPLICATION_VIEW_OPTIONS = Object.freeze([
  { key: APPLICATION_VIEWS.FACE, label: 'Rosto' },
  { key: APPLICATION_VIEWS.FRONT, label: 'Frente' },
  { key: APPLICATION_VIEWS.BACK, label: 'Costas' },
]);

const HEAD_FRAME = Object.freeze({ width: 1024, height: 1536 });

export function applicationViewLabel(view) {
  const found = APPLICATION_VIEW_OPTIONS.find((item) => item.key === view);
  return found ? found.label : view;
}

/** Imagem e quadro (largura x altura) de uma vista para a variante do paciente. */
export function getApplicationFigure(view, variant) {
  if (view === APPLICATION_VIEWS.FACE) {
    return { src: variant === 'female' ? headFemaleFront : headMaleFront, ...HEAD_FRAME };
  }
  const figure = getBodySilhouetteFigure(variant);
  return {
    src: view === APPLICATION_VIEWS.BACK ? figure.backSrc : figure.src,
    ...BODY_SILHOUETTE_VIEWBOX,
  };
}

/**
 * Fundo de uma vista: null usa o desenho; { attachmentId, crop } usa uma
 * foto do paciente (anexo de evolução) recortada. `crop` é o recorte em
 * frações da foto: x, y (canto superior esquerdo), w, h.
 */
export const FULL_CROP = Object.freeze({ x: 0, y: 0, w: 1, h: 1 });

// Recorte mínimo (fração da largura e da altura), igual ao backend
export const MIN_CROP_SIZE = 0.05;

const round4 = (value) => Math.round(value * 10000) / 10000;

export function normalizeCrop(crop) {
  if (!crop) return { ...FULL_CROP };
  const w = Math.min(1, Math.max(MIN_CROP_SIZE, Number(crop.w) || 1));
  const h = Math.min(1, Math.max(MIN_CROP_SIZE, Number(crop.h) || 1));
  const x = Math.min(1 - w, Math.max(0, Number(crop.x) || 0));
  const y = Math.min(1 - h, Math.max(0, Number(crop.y) || 0));
  return { x: round4(x), y: round4(y), w: round4(w), h: round4(h) };
}

/** Mesmo fundo (desenho, ou a mesma foto com o mesmo recorte): as marcações se sobrepõem. */
export function sameBackground(a, b) {
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (String(a.attachmentId) !== String(b.attachmentId)) return false;
  const cropA = normalizeCrop(a.crop);
  const cropB = normalizeCrop(b.crop);
  return ['x', 'y', 'w', 'h'].every((key) => Math.abs(cropA[key] - cropB[key]) < 0.001);
}
